'use client';

/**
 * KAN-270: Analytics provider — initializes Matomo and tracks page views.
 * KAN-305: Links Matomo visits to authenticated users via pseudonymized User ID.
 *
 * Matomo runs in cookieless mode (disableCookies) so no cookie consent
 * is required under ePrivacy. The provider:
 * 1. Calls initMatomo() once on mount (injects the Matomo JS tracker)
 * 2. Tracks SPA page views on Next.js route changes via usePathname
 * 3. Sets/resets the Matomo User ID on Supabase auth state changes
 *
 * The User ID is the Supabase UUID — already pseudonymized, never PII.
 * Matomo Cloud's "Pseudonymize User ID" setting hashes it again before storage.
 *
 * Silently does nothing if NEXT_PUBLIC_MATOMO_URL / NEXT_PUBLIC_MATOMO_SITE_ID
 * env vars are not set.
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { initMatomo, trackPageView, setUserId, resetUserId } from '@/lib/analytics/matomo';
import { createClient } from '@/lib/db/supabase-client';

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const initialized = useRef(false);
  const prevPathname = useRef<string | null>(null);

  // Initialize Matomo once on mount
  useEffect(() => {
    if (!initialized.current) {
      initMatomo();
      initialized.current = true;
    }
  }, []);

  // Track page views on route changes (skip initial — initMatomo already tracks first page)
  useEffect(() => {
    if (prevPathname.current !== null && prevPathname.current !== pathname) {
      trackPageView(pathname);
    }
    prevPathname.current = pathname;
  }, [pathname]);

  // KAN-305: Sync Matomo User ID with Supabase auth state
  useEffect(() => {
    const supabase = createClient();

    // Set User ID if already logged in when the provider mounts
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) {
        setUserId(session.user.id);
      }
    });

    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user?.id) {
          setUserId(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          resetUserId();
        }
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return <>{children}</>;
}
